import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, MoveUpRight, ShoppingCart, Sparkles, Tag } from 'lucide-react';
import Seo from '../components/Seo';
import { useI18n } from '../context/I18nContext';
import AnimatedEyebrow from '../components/AnimatedEyebrow';
import ImageOnlySkeletonLoader from '../components/ImageOnlySkeletonLoader';
import ShortcutsSection from '../components/ShortcutsSection';
import { formatTND } from '../lib/currency';
import { localizePath } from '../lib/localizedRoutes';
import { getActivePromo, getEffectivePrice, getPromoToneClasses, sortItemsByPromo } from '../lib/promo';
import { shouldShowPrices } from '../lib/priceVisibility';
import { absoluteUrl, getSiteUrl } from '../lib/seo';
import { DEFAULT_SHORTCUTS } from '../components/shortcutsData';

async function fetchJsonOrThrow(url) {
  const response = await fetch(url);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error('Request failed');
  }
  return data;
}

function buildPromoItem(item, language, entityType) {
  const activePromo = getActivePromo(item, language);
  if (!activePromo) return null;
  const id = String(item?._id || item?.id || '').trim();
  if (!id) return null;
  const basePrice = Number(item?.price ?? 0);
  const effectivePrice = getEffectivePrice(item, language);
  return {
    id,
    entityType,
    title: String(item?.name || '').trim(),
    category: entityType === 'product' ? String(item?.productType || '').trim() : String(item?.category || '').trim(),
    image: item?.images?.[0] || '/placeholders/1.webp',
    route: entityType === 'product' ? `/machines/${id}` : `/spare-parts/${id}`,
    basePrice,
    effectivePrice,
    hasDiscount: activePromo?.kind === 'price' && effectivePrice < basePrice,
    activePromo,
    promoTone: getPromoToneClasses(activePromo?.colorTone),
  };
}

function formatSurfaceLabel(value = '') {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function PromoCard({ item, language, t, showPrices }) {
  const typeLabel =
    item.entityType === 'product' ? t('home.promotions.productLabel') : t('home.promotions.sparePartLabel');

  return (
    <Link
      href={localizePath(item.route, language)}
      className="group relative block w-full overflow-hidden rounded-[26px] border border-white/10 bg-[var(--dash-black)] text-white transition-all duration-300 hover:border-[var(--dash-main)]/40 sm:grid sm:min-h-[220px] sm:grid-cols-[minmax(0,0.56fr)_minmax(0,0.44fr)] md:min-h-[280px] md:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]"
    >
      <div className="relative aspect-[16/11] overflow-hidden bg-[var(--dash-black)] sm:aspect-auto sm:min-h-full md:min-h-full">
        <ImageOnlySkeletonLoader
          image={item.image}
          mode="native"
          alt={item.title}
          containerClassName="h-full w-full"
          imgClassName="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-black/75 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/90 backdrop-blur-md">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--dash-main)]" />
          {typeLabel}
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-4 bg-[var(--dash-black)] p-4 sm:p-5 md:p-5">
        <div className="space-y-2">
          {item.category ? (
            <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-white/10 bg-white/8 px-3 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-sm">
              <Tag className="h-3.5 w-3.5 shrink-0 text-[var(--dash-main)]" />
              <span className="truncate text-[10px] font-semibold uppercase tracking-[0.22em] text-white/75">
                {formatSurfaceLabel(item.category)}
              </span>
            </div>
          ) : null}
          <h3 className="line-clamp-2 text-lg font-semibold leading-snug text-white md:text-xl">
            {item.title}
          </h3>
        </div>

        <div className="mt-auto space-y-3">
          {item.entityType === 'product' ? (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/40">
                {t('home.promotions.quoteOnRequest')}
              </p>
              <p className="text-base font-bold text-[var(--dash-other)]">
                {t('home.promotions.tailoredOffer')}
              </p>
            </div>
          ) : showPrices ? (
            item.hasDiscount ? (
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/40 line-through">
                  {formatTND(item.basePrice)}
                </p>
                <p className="text-base font-bold text-[var(--dash-other)]">
                  {formatTND(item.effectivePrice)}
                </p>
              </div>
            ) : (
              <p className="text-base font-bold text-[var(--dash-other)]">
                {formatTND(item.basePrice)}
              </p>
            )
          ) : (
            <p className="text-base font-bold text-[var(--dash-other)]">
              {t('common.priceOnRequest')}
            </p>
          )}

          <div className="flex items-center justify-between border-t border-white/10 pt-4">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
              <Sparkles className="h-3.5 w-3.5 text-[var(--dash-main)]" />
              {t('home.promotions.viewOffer')}
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--dash-main)] text-white transition-all duration-300 group-hover:bg-[var(--dash-other)] group-hover:text-[var(--dash-black)]">
              {item.entityType === 'product' ? <MoveUpRight className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function PromotionsPage() {
  const { language, t } = useI18n();
  const siteUrl = getSiteUrl();
  const title = language === 'fr' ? 'Promotions ETC Company' : 'ETC Company Promotions';
  const description =
    language === 'fr'
      ? 'Découvrez les offres et promotions en cours sur les machines et pièces de rechange ETC.'
      : 'Discover current offers and promotions on ETC machines and spare parts.';
  const [products, setProducts] = useState([]);
  const [spareParts, setSpareParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursorGlow, setCursorGlow] = useState({ x: 15, y: 20 });

  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    setCursorGlow({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    });
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    Promise.all([
      fetchJsonOrThrow(`/api/products?lang=${language}&paginated=false`),
      fetchJsonOrThrow(`/api/spare-parts?lang=${language}&paginated=false`),
    ])
      .then(([productsData, sparePartsData]) => {
        if (!isMounted) return;
        setProducts(Array.isArray(productsData) ? productsData : []);
        setSpareParts(Array.isArray(sparePartsData) ? sparePartsData : []);
      })
      .catch(() => {
        if (!isMounted) return;
        setProducts([]);
        setSpareParts([]);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [language]);

  const promoItems = useMemo(() => {
    const combined = [
      ...sortItemsByPromo(products.map((item) => buildPromoItem(item, language, 'product')).filter(Boolean)),
      ...sortItemsByPromo(spareParts.map((item) => buildPromoItem(item, language, 'sparepart')).filter(Boolean)),
    ];
    return sortItemsByPromo(combined).slice(0, 12);
  }, [language, products, spareParts]);
  const titleParts = useMemo(() => {
    const line1 = String(t('home.promotions.titleLine1') || '').trim();
    const line2 = String(t('home.promotions.titleLine2') || '').trim();
    return { line1, line2 };
  }, [t]);
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: language === 'fr' ? 'Accueil' : 'Home',
        item: absoluteUrl('/', siteUrl),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: title,
        item: absoluteUrl('/promotions', siteUrl),
      },
    ],
  };

  return (
    <div className="relative overflow-hidden bg-[var(--dash-blacker)]" onMouseMove={handleMouseMove}>
      <Seo
        title={title}
        description={description}
        jsonLd={[breadcrumbJsonLd]}
        keywords={{
          en: ['ETC promotions', 'special offers', 'discounted machines', 'spare parts deals', 'warehouse equipment'],
          fr: ['promotions ETC', 'offres spéciales', 'machines en promotion', 'pièces de rechange', 'matériel de manutention'],
        }}
      />
      <div className="pointer-events-none absolute inset-0 z-0 bg-[url('/bg/5.webp')] bg-no-repeat bg-cover bg-center opacity-20" />
      <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,93,16,0.16)_0%,transparent_26%)]" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 transition-[background-image] duration-150 ease-out"
        style={{
          backgroundImage: `radial-gradient(circle at ${cursorGlow.x}% ${cursorGlow.y}%, rgba(255,93,16,0.08) 0%, transparent 42%)`,
        }}
      />

      <section className="relative z-10 overflow-hidden pt-40 pb-20">
        <div className="container relative z-10 mx-auto px-6 lg:px-12">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-5 flex justify-center">
              <AnimatedEyebrow
                borderClassName="border-[var(--dash-main)]/40"
                surfaceClassName="bg-[var(--dash-black)]"
                dotClassName="bg-[var(--dash-main)] shadow-[0_0_10px_rgba(228,188,155,0.7)]"
              >
                {t('home.promotions.eyebrow')}
              </AnimatedEyebrow>
            </div>
            <h1 className="mx-auto mt-4 max-w-xl text-3xl font-light uppercase !leading-[1.2] tracking-tight text-white sm:text-4xl md:text-5xl xl:max-w-none">
              <span className="block xl:inline">{titleParts.line1}</span>
              <span className="block font-black bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent xl:inline xl:ml-3">
                {titleParts.line2}
              </span>
            </h1>
            <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-white/70">
              {t('home.promotions.description')}
            </p>
          </div>
        </div>
      </section>

      <section className="relative z-10 pb-40">
        <div className="container mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="grid gap-5 md:grid-cols-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="h-80 animate-pulse rounded-[26px] border border-white/10 bg-white/5" />
              ))}
            </div>
          ) : promoItems.length > 0 ? (
            <div className="grid gap-5 md:grid-cols-2">
              {promoItems.map((item) => {
                return (
                  <PromoCard
                    key={`${item.entityType}-${item.id}`}
                    item={item}
                    language={language}
                    t={t}
                    showPrices={shouldShowPrices()}
                  />
                );
              })}
            </div>
          ) : (
            <div className="rounded-[28px] border border-dashed border-white/15 bg-white/5 p-12 text-center">
              <p className="text-lg font-semibold text-white">{t('home.promotions.emptyTitle')}</p>
              <p className="mt-2 text-sm text-white/60">
                {t('home.promotions.emptyDescription')}
              </p>
            </div>
          )}
        </div>
      </section>

      <ShortcutsSection items={DEFAULT_SHORTCUTS} excludeCurrent />
    </div>
  );
}
